/**
 * AudioEngine.js — OPERATION BLACKOUT's entire soundscape.
 *
 * There are no audio files in this project. Every gunshot, footstep, impact, scream
 * and hitmarker is generated by the Web Audio API, pre-rendered into AudioBuffers at
 * construction through OfflineAudioContext, and played back as buffer sources with
 * per-instance pitch, level and filter variation.
 *
 * Signal flow
 * -----------
 *   AudioBuffer -> playbackRate -> voice gain -> air-absorption LP -> Panner (HRTF)
 *                                        |                              |
 *                                        +-> reverb send ------+        v
 *                                                              v    bus gain
 *                                                          Reverb -> master -> hearing
 *                                                                       -> muffle
 *                                                                       -> slow leveller
 *                                                                       -> tanh clip -> DC block
 *
 * Design notes worth knowing before editing:
 *
 * - Pre-rendering, not live synthesis. A gunshot is ~18 nodes; at 700 RPM that is
 *   200 node allocations a second before anything else in the game runs. Rendering
 *   four variations of each sound once at boot and then playing buffers costs a
 *   single BufferSource per shot, and the variation budget is spent where the ear
 *   actually notices it (pitch, level, filter, which variation).
 * - Variations are batched: all four variations of a sound render into ONE
 *   OfflineAudioContext laid out in adjacent time slots, then get sliced. Creating
 *   the context dominates the cost, so this is roughly an order of magnitude faster
 *   than one context per variation.
 * - The reverb send is taken PRE-panner. Sending post-HRTF would spatialise the room
 *   as if it were a point source, which is exactly backwards.
 * - Unknown ids are dropped silently. Other systems are allowed to emit speculative
 *   ids without coordinating with this file.
 */

import { Mixer, holdParam } from './Mixer.js';
import { Reverb } from './Reverb.js';
import { makeRng, renderSlots, finishBuffers, biquad, gain, chain, clamp, analyze } from './synth/dsp.js';
import { GUN_PRESETS, gunshot, WEAPON_MECH } from './synth/weapons.js';
import { impact, ricochet, explosion } from './synth/impacts.js';
import { step, jump, land, slide, mantle } from './synth/footsteps.js';
import { hurt, death, enemyPain, enemyDeath, enemyAlert, heartbeat } from './synth/voice.js';
import { uiHit, uiHeadshot, uiKill, uiSelect, tinnitusTone } from './synth/ui.js';

export const TUNING = {
  masterVolume: 0.9,
  maxVoices: { ultra: 36, high: 32, medium: 20 },
  /** HRTF is noticeably more expensive; drop to equal-power on weak hardware. */
  panningModel: { ultra: 'HRTF', high: 'HRTF', medium: 'equalpower' },
  variants: { ultra: 4, high: 4, medium: 3 },
  stepVariants: { ultra: 5, high: 5, medium: 3 },

  distance: { ref: 4, max: 160, rolloff: 1.15 },
  /** Air absorption: cutoff = airBase * exp(-dist * airFalloff). */
  airBase: 21000,
  airFalloff: 0.017,
  airFloor: 700,

  reverb: { preset: 'room', wet: 0.42, tone: 7200 },
  /** Reverb send multiplier as a function of distance: near .. far. */
  sendNear: 0.55,
  sendFar: 1.35,
  sendFarDistance: 30,

  duck: { amount: 0.6, attack: 0.02, hold: 0.09, release: 0.26 },

  hearing: {
    /** Fatigue added per unsuppressed player gunshot. */
    perShot: 0.055,
    maxFromGunfire: 0.55,
    /** Muffle cutoff at zero and full suppression. */
    openHz: 20000,
    closedHz: 1250,
    /** Volume dip at full suppression. */
    dip: 0.3,
    /** Tinnitus tone triggers above this suppression intensity. */
    ringThreshold: 0.34,
    recover: 2.4,
  },

  /** Minimum seconds between two plays of the same id; stops pellet spam stacking. */
  defaultGap: 0.012,
};

/* ------------------------------------------------------------------ recipes --- */

const hash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
};

/** Ids that are simply another id under a different name. */
const ALIASES = {
  step_flesh: 'step_dirt',
  step_grass: 'step_dirt',
  step_gravel: 'step_dirt',
  impact_grass: 'impact_dirt',
  impact_gravel: 'impact_dirt',
  shell_concrete: 'shell',
  shell_metal: 'shell',
  fire_pistol: 'fire_smg',
  reload_start: 'reload_mag_out',
  reload_end: 'reload_mag_in',
  hitmarker: 'ui_hit',
  explosion_far: 'explosion',
};

/**
 * The registry. Each entry: which bus it feeds, its mix level, how long a render slot
 * it needs, whether it is spatial, how much of it goes to the room, and the synth
 * function that draws it.
 */
function buildRecipes(quality) {
  const V = TUNING.variants[quality] ?? 4;
  const S = TUNING.stepVariants[quality] ?? 4;
  const R = [];
  const add = (id, o) => R.push({
    id, bus: 'world', gain: 1, variants: V, slot: 0.5, spatial: true,
    send: 0.35, jitter: 0.05, priority: 1, gap: TUNING.defaultGap, ...o,
  });

  // --- weapons ---------------------------------------------------------------
  for (const [id, preset] of Object.entries(GUN_PRESETS)) {
    add(id, {
      bus: id === 'enemy_fire' ? 'world' : 'weapons',
      // Peak-normalised buffers mean the recipe gain IS the mix. These were set from
      // measured output peaks, not by ear-guessing: gunfire sits ~14 dB above the
      // weapon mechanics and ~16 dB above footsteps, which is roughly the real ratio.
      // Sized so one shot peaks near -4 dBFS: comfortably under the soft clipper's
      // -2.8 dB knee, so a single round is never distorted, while a firefight is free
      // to push into it — which is where a clipper is supposed to earn its keep.
      gain: id === 'fire_smg' ? 0.66 : id === 'enemy_fire' ? 0.5 : 0.76,
      variants: V, slot: 1.35, send: id === 'enemy_fire' ? 0.7 : 0.45,
      jitter: 0.06, priority: 4, gap: 0.02,
      spatial: id === 'enemy_fire',
      build: (ac, dest, t, rng) => gunshot(ac, dest, t, rng, preset),
    });
  }

  const mech = [
    ['dryfire', 0.3, 0.17, WEAPON_MECH.dryfire],
    ['reload_mag_out', 0.5, 0.21, WEAPON_MECH.magOut],
    ['reload_mag_in', 0.42, 0.22, WEAPON_MECH.magIn],
    ['bolt_release', 0.38, 0.21, WEAPON_MECH.boltRelease],
    ['ads_in', 0.32, 0.12, WEAPON_MECH.adsIn],
    ['ads_out', 0.28, 0.12, WEAPON_MECH.adsOut],
    ['weapon_switch', 0.48, 0.18, WEAPON_MECH.weaponSwitch],
  ];
  for (const [id, slot, g, fn] of mech) {
    add(id, {
      bus: 'weapons', gain: g, slot, spatial: false, send: 0.16,
      jitter: 0.045, priority: 3, variants: Math.max(2, V - 1),
      build: (ac, dest, t, rng) => fn(ac, dest, t, rng),
    });
  }
  add('shell', {
    bus: 'weapons', gain: 0.17, slot: 0.6, spatial: true, send: 0.3,
    jitter: 0.09, priority: 1, variants: V, gap: 0.03,
    // Variations here genuinely differ in size (two bounces versus three), so keep
    // their relative levels rather than flattening them.
    norm: 'family', refDistance: 1.5, rolloff: 2.2,
    build: (ac, dest, t, rng) => WEAPON_MECH.shellCasing(ac, dest, t, rng, {}),
  });

  // --- impacts ---------------------------------------------------------------
  const impacts = {
    concrete: 0.5, metal: 0.65, wood: 0.35, dirt: 0.4,
    sand: 0.4, glass: 0.75, flesh: 0.4, water: 0.6,
  };
  for (const [mat, slot] of Object.entries(impacts)) {
    add(`impact_${mat}`, {
      gain: mat === 'flesh' ? 0.5 : 0.43, slot, send: 0.45,
      jitter: 0.09, priority: 2, gap: 0.028,
      refDistance: 3,
      build: (ac, dest, t, rng) => impact(ac, dest, t, rng, mat, 1),
    });
  }
  add('ricochet', {
    gain: 0.34, slot: 0.8, send: 0.55, jitter: 0.1, priority: 2, gap: 0.03,
    build: (ac, dest, t, rng) => ricochet(ac, dest, t, rng, 1),
  });
  add('explosion', {
    gain: 0.95, slot: 2.3, send: 0.8, jitter: 0.05, priority: 5,
    variants: Math.max(2, V - 2), refDistance: 12, rolloff: 0.7,
    build: (ac, dest, t, rng) => explosion(ac, dest, t, rng, 1),
  });

  // --- locomotion ------------------------------------------------------------
  const steps = { concrete: 0.4, metal: 0.55, wood: 0.4, dirt: 0.4, sand: 0.4, glass: 0.4, water: 0.5 };
  for (const [mat, slot] of Object.entries(steps)) {
    add(`step_${mat}`, {
      bus: 'player', gain: 0.17, slot, variants: S, send: 0.28,
      jitter: 0.09, priority: 0, spatial: false, gap: 0.05,
      build: (ac, dest, t, rng) => step(ac, dest, t, rng, mat, {}),
    });
  }
  add('enemy_step', {
    bus: 'world', gain: 0.14, slot: 0.4, variants: S, send: 0.4,
    jitter: 0.1, priority: 0, spatial: true, gap: 0.04, refDistance: 2.5, rolloff: 1.6,
    build: (ac, dest, t, rng) => step(ac, dest, t, rng, 'concrete', { level: 0.85 }),
  });
  add('jump', {
    bus: 'player', gain: 0.14, slot: 0.4, spatial: false, send: 0.22, priority: 1,
    variants: Math.max(2, V - 1),
    build: (ac, dest, t, rng) => jump(ac, dest, t, rng),
  });
  add('land', {
    bus: 'player', gain: 0.34, slot: 0.6, spatial: false, send: 0.3, priority: 2,
    variants: Math.max(2, V - 1),
    build: (ac, dest, t, rng) => land(ac, dest, t, rng),
  });
  add('slide', {
    bus: 'player', gain: 0.22, slot: 1.25, spatial: false, send: 0.3, priority: 2,
    variants: Math.max(2, V - 2), jitter: 0.03, gap: 0.4,
    build: (ac, dest, t, rng) => slide(ac, dest, t, rng),
  });
  add('mantle', {
    bus: 'player', gain: 0.18, slot: 0.7, spatial: false, send: 0.25, priority: 2,
    variants: Math.max(2, V - 1),
    build: (ac, dest, t, rng) => mantle(ac, dest, t, rng),
  });

  // --- voice -----------------------------------------------------------------
  const voices = [
    ['hurt', 0.65, 'player', false, 0.2, hurt],
    ['death', 1.8, 'player', false, 0.3, death],
    ['enemy_pain', 0.75, 'world', true, 0.5, enemyPain],
    ['enemy_death', 1.8, 'world', true, 0.55, enemyDeath],
    ['enemy_alert', 1.15, 'world', true, 0.5, enemyAlert],
    ['heartbeat', 0.6, 'player', false, 0.05, heartbeat],
  ];
  for (const [id, slot, bus, spatial, send, fn] of voices) {
    add(id, {
      bus, gain: id === 'heartbeat' ? 0.34 : id.startsWith('enemy') ? 0.45 : 0.55, slot, spatial, send,
      jitter: 0.05, priority: 3, variants: Math.max(2, V - 1), gap: 0.08,
      build: (ac, dest, t, rng) => fn(ac, dest, t, rng),
    });
  }

  // --- ui --------------------------------------------------------------------
  const uis = [
    ['ui_hit', 0.16, uiHit, 0.6],
    ['ui_headshot', 0.32, uiHeadshot, 0.68],
    ['ui_kill', 0.6, uiKill, 0.75],
    ['ui_select', 0.16, uiSelect, 0.3],
  ];
  for (const [id, slot, fn, g] of uis) {
    add(id, {
      bus: 'ui', gain: g, slot, spatial: false, send: 0, jitter: 0.02,
      priority: 4, variants: 2, gap: 0.02,
      build: (ac, dest, t, rng) => fn(ac, dest, t, rng),
    });
  }

  return R;
}

/* ------------------------------------------------------------------- engine --- */

export class AudioEngine {
  constructor(ctx) {
    this.ctx = ctx;
    this.quality = ctx?.quality?.level ?? 'high';
    this.enabled = true;
    this.ready = false;
    this.buildMs = 0;

    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) {
      console.warn('[audio] Web Audio unavailable — running silent');
      this.available = false;
      this._noop();
      return;
    }
    this.available = true;

    // Created immediately, which is legal — it simply starts 'suspended' under the
    // autoplay policy and stays that way until a gesture resumes it. Everything
    // below (including offline pre-rendering) works fine while suspended.
    this.ac = new AC({ latencyHint: 'interactive' });
    this.sampleRate = this.ac.sampleRate;

    this._masterVolume = TUNING.masterVolume;
    this.mixer = new Mixer(this.ac, { master: this._masterVolume });
    this.reverb = new Reverb(this.ac, TUNING.reverb.preset);
    this.reverb.output.gain.value = TUNING.reverb.wet;
    this.reverb.setTone(TUNING.reverb.tone, 0);
    this.mixer.connectReverb(this.reverb);

    this.maxVoices = TUNING.maxVoices[this.quality] ?? 32;
    this.panningModel = TUNING.panningModel[this.quality] ?? 'HRTF';

    this.recipes = new Map();
    this.bank = new Map();
    this.voices = [];
    this._lastPlay = new Map();
    this._listener = { x: 0, y: 1.65, z: 0 };
    this._hearing = { level: 0, recover: TUNING.hearing.recover, ringAt: -1 };
    this._env = { wet: 1 };

    for (const r of buildRecipes(this.quality)) this.recipes.set(r.id, r);
    this.ids = [...this.recipes.keys()];

    this._bindEvents();
    this._bindGesture();
    this._unregister = ctx?.engine?.registerUpdate?.((dt, el) => this.update(dt, el), 'ui');

    this.readyPromise = this._buildBank().catch((err) => {
      console.error('[audio] bank build failed', err);
    });
  }

  /** Silent stand-in when Web Audio is missing, so callers never need to null-check. */
  _noop() {
    this.play = () => null;
    this.update = () => {};
    this.setMasterVolume = () => {};
    this.suppressHearing = () => {};
    this.readyPromise = Promise.resolve();
    this.ids = [];
  }

  /* ------------------------------------------------------------- lifecycle --- */

  _bindEvents() {
    const ev = this.ctx?.events;
    if (!ev) return;
    this._offs = [
      ev.on('audio:play', (p) => {
        if (!p || typeof p.id !== 'string') return;
        this.play(p.id, p);
      }),
      // Shell casings are a signature FPS sound and the FX system already tells us
      // exactly where each one lands, so there is no reason to make it emit a second
      // event just to get a noise out of it.
      ev.on('fx:shell', (p) => this.play('shell', { position: p?.position, volume: 0.55, delay: 0.34 })),
      ev.on('game:state', () => this.resume()),
    ];
  }

  /**
   * Autoplay policy: the context cannot start until a user gesture. The game calls
   * input.requestLock() on canvas click, so the same gesture resumes us. Listeners
   * stay attached until a resume actually succeeds — the first click is sometimes
   * consumed by pointer-lock negotiation.
   */
  _bindGesture() {
    if (typeof window === 'undefined') return;
    const kick = () => {
      this.resume().then(() => {
        if (this.ac.state === 'running') this._unbindGesture();
      });
    };
    this._gesture = kick;
    for (const type of ['pointerdown', 'mousedown', 'click', 'keydown', 'touchstart']) {
      window.addEventListener(type, kick, { passive: true, capture: true });
    }
  }

  _unbindGesture() {
    if (!this._gesture) return;
    for (const type of ['pointerdown', 'mousedown', 'click', 'keydown', 'touchstart']) {
      window.removeEventListener(type, this._gesture, { capture: true });
    }
    this._gesture = null;
  }

  async resume() {
    if (!this.available) return;
    try {
      if (this.ac.state === 'suspended') await this.ac.resume();
    } catch { /* still suspended; play() will simply no-op */ }
  }

  async suspend() {
    if (!this.available) return;
    try { if (this.ac.state === 'running') await this.ac.suspend(); } catch { /* ignore */ }
  }

  /* ---------------------------------------------------------- bank building --- */

  async _buildBank() {
    const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
    const list = [...this.recipes.values()];
    // Chunked so a long build yields to the main thread between groups instead of
    // holding the boot screen frozen.
    const CHUNK = 8;
    for (let i = 0; i < list.length; i += CHUNK) {
      const chunk = list.slice(i, i + CHUNK);
      await Promise.all(chunk.map(async (rec) => {
        try {
          const seed = hash(rec.id);
          const raw = await renderSlots(this.sampleRate, rec.variants, rec.slot, (oac, dest, t, v) => {
            rec.build(oac, dest, t, makeRng(seed + v * 7919 + 1));
          });
          this.bank.set(rec.id, finishBuffers(this.ac, raw, { peak: 0.92, mode: rec.norm ?? 'each' }));
        } catch (err) {
          console.warn(`[audio] failed to render "${rec.id}"`, err);
          this.bank.set(rec.id, []);
        }
      }));
      await new Promise((r) => setTimeout(r, 0));
    }
    this.buildMs = (typeof performance !== 'undefined' ? performance : Date).now() - t0;
    this.ready = true;
    this.ctx?.events?.emit?.('audio:ready', { ms: this.buildMs, ids: this.ids.length });
    return this;
  }

  _resolve(id) {
    return this.recipes.has(id) ? id : (ALIASES[id] ?? id);
  }

  getBuffers(id) {
    return this.bank.get(this._resolve(id)) ?? [];
  }

  /* ------------------------------------------------------------------- play --- */

  /**
   * @param {string} id
   * @param {object} [opts] { position, volume, pitch, delay, bus }
   * @returns {object|null} the voice handle, or null if dropped
   */
  play(id, opts = {}) {
    if (!this.available || !this.enabled || !this.ready) return null;
    const key = this._resolve(id);
    const rec = this.recipes.get(key);
    if (!rec) return null; // unknown ids fail silently, by contract

    const now = this.ac.currentTime;
    const last = this._lastPlay.get(key) ?? -1;
    if (now - last < (rec.gap ?? TUNING.defaultGap)) return null;
    this._lastPlay.set(key, now);

    const voice = this._spawn(this.ac, this.mixer, this.reverb, rec, opts, now, true);
    if (!voice) return null;

    // The player's own gunfire ducks the world and fatigues the ear.
    if (rec.bus === 'weapons' && key.startsWith('fire_')) {
      const d = TUNING.duck;
      this.mixer.duck('world', d.amount, d.attack, d.hold, d.release);
      this.suppressHearing(TUNING.hearing.perShot, 1.6, { additive: true, max: TUNING.hearing.maxFromGunfire });
    }
    return voice;
  }

  /**
   * Builds one playing voice. Deliberately context-agnostic: the same code path
   * serves live playback and the offline verification renders, so what the test
   * harness measures is literally what the player hears.
   */
  _spawn(ac, mixer, reverb, rec, opts, when, track) {
    const bufs = this.bank.get(rec.id);
    if (!bufs || bufs.length === 0) return null;

    const pool = track ? Math.random() : 0.5;
    const buf = bufs[Math.min(bufs.length - 1, (pool * bufs.length) | 0)];
    const t = when + (opts.delay ?? 0);

    const rnd = track ? Math.random : () => 0.5;
    const pitch = clamp((opts.pitch ?? 1) * (1 + (rnd() - 0.5) * (rec.jitter ?? 0.05)), 0.25, 4);
    const level = clamp(opts.volume ?? 1, 0, 4) * rec.gain * (1 + (rnd() - 0.5) * 0.12);

    // Distance drives three things at once: the panner's own attenuation, the air
    // absorption filter, and how much of the signal reaches the room.
    const pos = opts.position && (opts.position.x !== undefined || Array.isArray(opts.position))
      ? (Array.isArray(opts.position)
        ? { x: opts.position[0], y: opts.position[1], z: opts.position[2] }
        : opts.position)
      : null;
    const spatial = rec.spatial && !!pos;
    let dist = 0;
    if (spatial) {
      const L = this._listener;
      dist = Math.hypot(pos.x - L.x, pos.y - L.y, pos.z - L.z);
    }

    const src = ac.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = pitch;

    const vg = gain(ac, level);
    const air = biquad(ac, 'lowpass',
      spatial ? clamp(TUNING.airBase * Math.exp(-dist * TUNING.airFalloff), TUNING.airFloor, TUNING.airBase) : 20000,
      0.7);
    chain(src, vg, air);

    let tail = air;
    let panner = null;
    if (spatial) {
      panner = ac.createPanner();
      panner.panningModel = this.panningModel;
      panner.distanceModel = 'inverse';
      panner.refDistance = rec.refDistance ?? TUNING.distance.ref;
      panner.maxDistance = rec.maxDistance ?? TUNING.distance.max;
      panner.rolloffFactor = rec.rolloff ?? TUNING.distance.rolloff;
      setPos(panner, pos.x, pos.y, pos.z, ac.currentTime);
      air.connect(panner);
      tail = panner;
    } else if (ac.createStereoPanner) {
      // A hair of width so 2D sounds do not sit in a hard mono point.
      const sp = ac.createStereoPanner();
      sp.pan.value = (rnd() - 0.5) * (rec.bus === 'ui' ? 0.1 : 0.35);
      air.connect(sp);
      tail = sp;
    }
    tail.connect(mixer.bus(opts.bus ?? rec.bus));

    // Reverb send, taken PRE-panner so the room is fed a mono point source.
    let send = null;
    if (rec.send > 0 && reverb) {
      const near = TUNING.sendNear;
      const far = TUNING.sendFar;
      const k = spatial ? clamp(dist / TUNING.sendFarDistance, 0, 1) : 0.15;
      send = gain(ac, rec.send * this._env.wet * (near + (far - near) * k));
      air.connect(send);
      send.connect(reverb.input);
    }

    src.start(t);

    const voice = {
      id: rec.id, src, vg, panner, air, send, t,
      priority: rec.priority ?? 1,
      level: level / (1 + dist * 0.12),
      released: false,
    };

    if (track) {
      src.onended = () => this._release(voice);
      this.voices.push(voice);
      if (this.voices.length > this.maxVoices) this._cull();
    }
    return voice;
  }

  _release(voice) {
    if (voice.released) return;
    voice.released = true;
    try {
      voice.src.disconnect();
      voice.vg.disconnect();
      voice.air.disconnect();
      voice.panner?.disconnect();
      voice.send?.disconnect();
    } catch { /* already torn down */ }
    const i = this.voices.indexOf(voice);
    if (i !== -1) this.voices.splice(i, 1);
  }

  /**
   * Voice stealing. Uncapped voices are not a subtle problem: a few hundred
   * simultaneous BufferSources will crackle and then stall the audio thread outright.
   * Score = audible level, weighted down by priority and age, and the lowest loses.
   */
  _cull() {
    const now = this.ac.currentTime;
    while (this.voices.length > this.maxVoices) {
      let worst = null;
      let worstScore = Infinity;
      for (const v of this.voices) {
        const age = now - v.t;
        const score = v.level * (1 + v.priority) / (1 + age * 3);
        if (score < worstScore) { worstScore = score; worst = v; }
      }
      if (!worst) break;
      // 8 ms fade rather than a hard stop; cutting a waveform mid-cycle is a click.
      try {
        holdParam(worst.vg.gain, now);
        worst.vg.gain.linearRampToValueAtTime(0, now + 0.008);
        worst.src.stop(now + 0.01);
      } catch { /* already stopped */ }
      this._release(worst);
    }
  }

  /* ------------------------------------------------------------------ mixer --- */

  setMasterVolume(v) {
    this._masterVolume = clamp(v, 0, 2);
    if (this.available) this.mixer.setMaster(this._masterVolume, 0.08);
  }

  getMasterVolume() {
    return this._masterVolume;
  }

  setBusVolume(name, v) {
    if (this.available) this.mixer.setBus(name, v);
  }

  /** Swap the room. `wet` 0..1 scales every send. */
  setEnvironment({ preset, wet, tone } = {}) {
    if (!this.available) return;
    if (preset) this.reverb.setPreset(preset);
    if (tone !== undefined) this.reverb.setTone(tone);
    if (wet !== undefined) this._env.wet = clamp(wet, 0, 2);
  }

  setEnabled(on) {
    this.enabled = !!on;
    if (this.available) this.mixer.setMaster(on ? this._masterVolume : 0, 0.1);
  }

  /* --------------------------------------------------------------- hearing --- */

  /**
   * Temporary hearing loss. Above `ringThreshold` it also starts a tinnitus tone,
   * routed past the muffle filter so it is not itself muffled — which is precisely
   * how it works in an ear that has just been next to a muzzle blast.
   *
   * @param {number} intensity 0..1
   * @param {number} duration  seconds to recover
   */
  suppressHearing(intensity, duration = TUNING.hearing.recover, { additive = false, max = 1 } = {}) {
    if (!this.available) return;
    const h = this._hearing;
    const want = clamp(intensity, 0, 1);
    h.level = additive ? Math.min(max, h.level + want) : Math.max(h.level, want);
    h.recover = Math.max(0.2, duration);

    const now = this.ac.currentTime;
    if (want >= TUNING.hearing.ringThreshold && now - h.ringAt > 0.6) {
      h.ringAt = now;
      tinnitusTone(this.ac, this.mixer.compressor, now + 0.01, duration * 0.9, 0.045 * want);
    }
  }

  get hearingLevel() {
    return this._hearing.level;
  }

  /* ----------------------------------------------------------------- update --- */

  /** Runs on the 'ui' phase, after the camera has settled for the frame. */
  update(dt) {
    if (!this.available) return;

    // --- listener ---
    const cam = this.ctx?.camera;
    if (cam?.matrixWorld) {
      const m = cam.matrixWorld.elements;
      const L = this.ac.listener;
      const now = this.ac.currentTime;
      this._listener.x = m[12];
      this._listener.y = m[13];
      this._listener.z = m[14];
      // Column-major: columns 0,1,2 are the right/up/back axes. Forward is -Z.
      if (L.positionX) {
        const tc = 0.012;
        L.positionX.setTargetAtTime(m[12], now, tc);
        L.positionY.setTargetAtTime(m[13], now, tc);
        L.positionZ.setTargetAtTime(m[14], now, tc);
        L.forwardX.setTargetAtTime(-m[8], now, tc);
        L.forwardY.setTargetAtTime(-m[9], now, tc);
        L.forwardZ.setTargetAtTime(-m[10], now, tc);
        L.upX.setTargetAtTime(m[4], now, tc);
        L.upY.setTargetAtTime(m[5], now, tc);
        L.upZ.setTargetAtTime(m[6], now, tc);
      } else {
        L.setPosition(m[12], m[13], m[14]);
        L.setOrientation(-m[8], -m[9], -m[10], m[4], m[5], m[6]);
      }
    }

    // --- hearing recovery ---
    const h = this._hearing;
    if (h.level > 0.0005) {
      h.level = Math.max(0, h.level - dt / h.recover);
      const T = TUNING.hearing;
      const f = T.openHz * Math.pow(T.closedHz / T.openHz, h.level);
      const now = this.ac.currentTime;
      this.mixer.muffle.frequency.setTargetAtTime(f, now, 0.05);
      this.mixer.hearing.gain.setTargetAtTime(1 - T.dip * h.level, now, 0.05);
    } else if (h.level !== 0) {
      h.level = 0;
      const now = this.ac.currentTime;
      this.mixer.muffle.frequency.setTargetAtTime(TUNING.hearing.openHz, now, 0.1);
      this.mixer.hearing.gain.setTargetAtTime(1, now, 0.1);
    }
  }

  /* ------------------------------------------------------------ diagnostics --- */

  get stats() {
    return {
      ready: this.ready,
      state: this.ac?.state,
      voices: this.voices.length,
      maxVoices: this.maxVoices,
      buildMs: Math.round(this.buildMs),
      sounds: this.ids.length,
      buffers: [...this.bank.values()].reduce((n, b) => n + b.length, 0),
      limiterDb: this.mixer ? Math.round(this.mixer.reductionDb * 10) / 10 : 0,
      hearing: Math.round(this._hearing.level * 100) / 100,
    };
  }

  /**
   * Render one id through the full playback chain — mixer, dynamics, limiter, room —
   * into an offline buffer. This is how the soundscape is verified without listening:
   * the harness measures peak, RMS, attack, decay, tail and spectral centroid of
   * exactly the signal that reaches the output device.
   */
  async renderOffline(id, { seconds = 2.0, spatial = false, distance = 6, volume = 1, pitch = 1 } = {}) {
    const key = this._resolve(id);
    const rec = this.recipes.get(key);
    if (!rec || !this.bank.has(key)) return null;

    const sr = this.sampleRate;
    const oac = new OfflineAudioContext(2, Math.ceil(seconds * sr), sr);
    const mixer = new Mixer(oac, { master: this._masterVolume });
    const reverb = new Reverb(oac, TUNING.reverb.preset);
    reverb.output.gain.value = TUNING.reverb.wet;
    reverb.tone.frequency.value = TUNING.reverb.tone;
    mixer.connectReverb(reverb);

    const opts = { volume, pitch };
    if (spatial) opts.position = { x: 0, y: 0, z: -distance };
    // `track: false` keeps the RNG at its midpoint so renders are reproducible.
    this._spawn(oac, mixer, reverb, { ...rec, spatial: rec.spatial && spatial }, opts, 0.02, false);
    return oac.startRendering();
  }

  /** Convenience for the harness: render + measure in one call. */
  async measure(id, opts) {
    const buf = await this.renderOffline(id, opts);
    return buf ? { id, ...analyze(buf), buffer: buf } : null;
  }

  dispose() {
    this._unregister?.();
    this._unbindGesture();
    for (const off of this._offs ?? []) off?.();
    for (const v of this.voices.slice()) {
      try { v.src.stop(); } catch { /* already stopped */ }
      this._release(v);
    }
    this.bank.clear();
    try {
      this.reverb?.dispose();
      this.mixer?.dispose();
      this.ac?.close();
    } catch { /* already closed */ }
  }
}

/** PannerNode position, using AudioParams where the browser exposes them. */
function setPos(panner, x, y, z, now) {
  if (panner.positionX) {
    panner.positionX.setValueAtTime(x, now);
    panner.positionY.setValueAtTime(y, now);
    panner.positionZ.setValueAtTime(z, now);
  } else {
    panner.setPosition(x, y, z);
  }
}
